import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, Globe, Shield, Plane, CreditCard, 
  Heart, AlertTriangle, ExternalLink, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

const resources = [
  {
    icon: FileText,
    title: 'Visa Requirements',
    description: 'Check visa requirements for your destination country before traveling.',
    link: 'https://travel.state.gov/content/travel/en/us-visas.html',
    linkText: 'U.S. State Department Visas'
  },
  {
    icon: Shield,
    title: 'CDC Travel Notices',
    description: 'Stay informed about health notices and vaccination requirements worldwide.',
    link: 'https://wwwnc.cdc.gov/travel/notices',
    linkText: 'CDC Travel Health Notices'
  },
  {
    icon: AlertTriangle,
    title: 'Travel Advisories',
    description: 'Check the latest travel advisories for your destination.',
    link: 'https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html/',
    linkText: 'State Department Advisories'
  },
  {
    icon: Plane,
    title: 'TSA Information',
    description: 'Learn about security rules, what you can bring, and TSA PreCheck.',
    link: 'https://www.tsa.gov/travel',
    linkText: 'TSA Travel Tips'
  },
  {
    icon: CreditCard,
    title: 'Travel Insurance',
    description: 'Protect your trip with comprehensive travel insurance coverage.',
    link: 'https://www.travelinsurance.com/',
    linkText: 'Compare Travel Insurance'
  },
  {
    icon: Heart,
    title: 'Travel Health',
    description: 'Find health information and vaccination recommendations for travelers.',
    link: 'https://wwwnc.cdc.gov/travel/destinations/list',
    linkText: 'CDC Destinations'
  }
];

const tips = [
  {
    title: 'Passport Tips',
    items: [
      'Check that your passport is valid for at least 6 months beyond your travel dates',
      'Make copies of your passport and store separately from the original',
      'Consider enrolling in STEP (Smart Traveler Enrollment Program)',
      'Apply for passport renewals at least 3-4 months before travel'
    ]
  },
  {
    title: 'Packing Tips',
    items: [
      'Roll clothes instead of folding to save space',
      'Pack a change of clothes in your carry-on',
      'Bring medications in original containers',
      'Check baggage allowances for your airline'
    ]
  },
  {
    title: 'Money Tips',
    items: [
      'Notify your bank of travel dates to avoid card blocks',
      'Carry a mix of cash and cards',
      'Know the exchange rate before you go',
      'Consider a credit card with no foreign transaction fees'
    ]
  }
];

const TravelResourcesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-navy py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-gold font-medium tracking-widest text-sm uppercase">Be Prepared</span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mt-4 mb-6">
            Travel Resources
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto text-lg">
            Essential information and links to help you prepare for your trip. 
            From visa requirements to health notices, we've got you covered.
          </p>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl text-navy mb-8 text-center">
            Important Travel Links
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((resource, index) => (
              <div 
                key={index}
                className="bg-cream rounded-2xl p-6 hover:shadow-card-hover transition-all duration-300"
              >
                <div className="w-12 h-12 bg-navy/10 rounded-xl flex items-center justify-center mb-4">
                  <resource.icon className="w-6 h-6 text-navy" />
                </div>
                <h3 className="font-semibold text-navy text-lg mb-2">{resource.title}</h3>
                <p className="text-text-secondary text-sm mb-4">{resource.description}</p>
                <a 
                  href={resource.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-gold hover:text-gold-dark font-medium text-sm"
                >
                  {resource.linkText}
                  <ExternalLink className="w-4 h-4 ml-1" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl text-navy mb-8 text-center">
            Travel Tips from Brandi
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {tips.map((tip, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-card">
                <h3 className="font-serif text-xl text-navy mb-4">{tip.title}</h3>
                <ul className="space-y-3">
                  {tip.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-2 text-text-secondary text-sm">
                      <Globe className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Need Help CTA */}
      <section className="py-16 bg-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Have Questions About Your Destination?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            I've traveled to 26+ countries and can provide firsthand advice about your destination.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/consultation">
              <Button className="bg-gold hover:bg-gold-dark text-navy font-semibold px-8 py-4 rounded-full">
                Book a Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-navy px-8 py-4 rounded-full">
                Ask a Question
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TravelResourcesPage;
